import { RequireAtLeastOne, UnboxCollection } from "@skylab-shared/types";
import { Calc, MidpointRounding } from "@skylab-shared/utils";
import { CalculatedDocument, DocumentCalculationContext, DocumentCalculator, DocumentItemForDocument, summariseDocument } from "@skylab/calculations/document";

type VatTableItem = UnboxCollection<CalculatedDocument['vatTableItems']>;

export abstract class OrderDocumentCalculator<TItem extends DocumentItemForDocument = DocumentItemForDocument, TContext extends DocumentCalculationContext = DocumentCalculationContext> extends DocumentCalculator {
    public override recalculate(items: TItem[], documentContext: TContext): CalculatedDocument {
        const groupedItems = this.groupItemsByVatRate(items);
        const vatTableItems = Object.keys(groupedItems).reduce((result, vatRateId) => {
            const items = groupedItems[vatRateId as any];
            const vatTableItem = this.sumItems(items, documentContext.precision);
            result.push(vatTableItem);
            return result;
        }, <CalculatedDocument['vatTableItems']>[]);
        return summariseDocument(vatTableItems, documentContext.precision);
    }
}

type NetItem = RequireAtLeastOne<DocumentItemForDocument, 'netValue'>;
export class OrderDocumentNetCalculator extends OrderDocumentCalculator<NetItem> {
    protected sumItems(items: NetItem[], precision: number): VatTableItem {
        const [{ vatRate, vatRateId }] = items;
        const netValue = Calc.round(items.reduce((sum, { netValue }) => sum + netValue, 0), precision, MidpointRounding.AwayFromZero);
        const vatValue = Calc.round(netValue * vatRate, precision, MidpointRounding.AwayFromZero);
        const grossValue = Calc.round(netValue + vatValue, precision, MidpointRounding.AwayFromZero);
        const cost = Calc.round(items.reduce((sum, { cost }) => sum + cost!, 0), precision, MidpointRounding.AwayFromZero);
        return {
            cost,
            vatRate,
            vatRateId,
            netValue,
            vatValue,
            grossValue
        };
    }
}

type GrossItem = RequireAtLeastOne<DocumentItemForDocument, 'grossValue'>;
export class OrderDocumentGrossCalculator extends OrderDocumentCalculator<GrossItem> {
    protected sumItems(items: GrossItem[], precision: number): VatTableItem {
        const [{ vatRate, vatRateId }] = items;
        const grossValue = Calc.round(items.reduce((sum, { grossValue }) => sum + grossValue, 0), precision, MidpointRounding.AwayFromZero);
        const vatValue = Calc.round(grossValue * vatRate / (1 + vatRate), precision, MidpointRounding.AwayFromZero);
        const netValue = Calc.round(grossValue - vatValue, precision, MidpointRounding.AwayFromZero);
        const cost = Calc.round(items.reduce((sum, { cost }) => sum + cost!, 0), precision, MidpointRounding.AwayFromZero);
        return {
            cost,
            vatRate,
            vatRateId,
            netValue,
            vatValue,
            grossValue
        };
    }
}